<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  provide,
  ref,
  toValue,
  useAttrs,
  useTemplateRef,
  watch,
} from 'vue'
import { useRafFn, useDraggable, useElementSize } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useApi } from '@/composables/api'
import { useUiStore } from '@/stores/ui'

import Thd from './th.vue'
import List from './list.vue'

const { t } = useI18n()

const props = defineProps({
  columns: Array,
  total: [Number, undefined],
  data: Array,
  listName: String,
  pagination: { type: Boolean, default: true },
  persist: { type: Boolean, default: true },
  pageSizeOptions: { type: Array, default: undefined },
  persistKey: { type: String, default: '' },
  /** Обработчик клика по строке (приоритет над @click:row из attrs) */
  onRowClick: { type: Function, default: null },
  /** Классы строки: строка, объект или функция row => классы */
  rowClass: { type: [String, Object, Function], default: null },
  /** Не показывать фильтры и сортировки в заголовках колонок */
  noHeaderFilters: { type: Boolean, default: false },
  /** Кнопка «Настроить список» вынесена наружу (в шапку родителя) — не рендерить её здесь */
  configButtonInHeader: { type: Boolean, default: false },
})
const model = defineModel()

const emit = defineEmits(['configModeChange'])
defineExpose({ openConfigMode })
const attrs = useAttrs()

provide('set-column-width-for-filter', setColumnWidthForFilter)

const footerScroll = useTemplateRef('footerScroll')
const footerScrollInner = useTemplateRef('footerScrollInner')
const elTable = useTemplateRef('el-table')
const elTableWrap = useTemplateRef('el-table-wrap')
const elScroll = useTemplateRef('el-scroll')
const elTableCard = useTemplateRef('el-table-card')
const elTableRightHandle = useTemplateRef('table-right-handle')
let colsRendered = 0
let tableX = 0
let normalModeWidthsSnapshot = null
let rightEdgeScrollCleanup = null
let footerScrollCleanup = null
let scrollRafId = null
let rightEdgeHandleEl = null
let rightEdgePointerMove = null
let rightEdgePointerUp = null
const MIN_COL_WIDTH = 100
/** Минимальная ширина столбца «Количество», чтобы в поле ввода помещалось 6 цифр (в т.ч. в режиме Настройки) */
const MIN_EXPENSE_AMOUNT_WIDTH = 100
const RIGHT_EDGE_EPS = 4
const DRAG_HANDLE_HALF = 6
const emptyDragImage =
  typeof document !== 'undefined'
    ? (() => {
        const img = document.createElement('img')
        img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
        return img
      })()
    : null

const initialState = new Map()
const state = new Map()
/** При открытии фильтра по дате — временно расширяем столбец; при закрытии — восстанавливаем ширину. Поддержка нескольких столбцов с фильтром дат. */
const filterRestoreWidths = new Map()

const columnOrder = ref([])
const hidden = ref([])
/** Храним последний известный набор заголовков колонок, чтобы не сбрасывать состояние при той же совокупности (убирает дёргание при передаче нового массива из computed) */
const lastColumnTitles = ref(null)
const listConfigModel = ref()
const configMode = ref(false)
const draggedColumnTitle = ref(null)
const dropTargetTitle = ref(null)
const rightEdgeDragging = ref(false)
const rightEdgeStartClientX = ref(null)
const rightEdgeStartContainerWidth = ref(null)
/** Ширины столбцов до сужения — для восстановления при расширении (слева направо до исходных, затем рост последнего) */
const rightEdgeSavedWidths = ref(null)
/** Ручка видима только когда горизонтальный скролл у правого края */
const rightHandleVisible = ref(false)
const { fullscreen } = storeToRefs(useUiStore())

const colsHidden = computed(() => {
  const cols = props.columns
  return hidden.value.map(key => cols.find(c => c.title == key))
})

const columnTitles = computed(() => new Set(props.columns.map(col => col.title)))

const rowClickable = computed(() => getRowClickHandler() != null)

const cols = computed(() => {
  const order = columnOrder.value
  const hiddenSet = new Set(hidden.value)
  const byTitle = Object.fromEntries(props.columns.map(c => [c.title, c]))
  return order
    .filter(title => !hiddenSet.has(title))
    .map(title => byTitle[title])
    .filter(Boolean)
})

/** Количество видимых нестатических колонок (обязательно должен оставаться хотя бы один) */
const visibleNonStaticCount = computed(() => cols.value.filter(c => !c.static).length)

const { refresh: createListConfig, pending: listConfigCreating } = useApi({
  url: 'list_configs',
  model: listConfigModel,
  method: 'post',
})

const { refresh: listConfigFetch } = useApi({
  url: `list_configs/my/${props.listName}`,
  model: listConfigModel,
})

const { pause: animationPause, resume: animationResume } = useRafFn(
  () => {
    if (!elTable.value) {
      return
    }

    const keysOrdered = cols.value.map(col => col.title)
    let tw = 0
    keysOrdered.forEach(key => {
      const item = state.get(key)
      if (!item) {
        return
      }

      const colDef = props.columns.find(c => c.title === key)
      const minwidth = colDef?.width || MIN_COL_WIDTH
      const defW = colDef ? parseColumnWidth(colDef.width) : null
      const staticW = getStaticColumnWidth(key)

      let rawW = staticW
      if (!rawW) {
        rawW = Number(item.width)
        if (isNaN(rawW)) {
          rawW = null
        }
      }
      if (!rawW) {
        rawW = defW
      }
      if (!rawW) {
        rawW = MIN_COL_WIDTH
      }

      let w = staticW ?? Math.max(rawW, getMinColWidth(key))
      if (w < minwidth) {
        w = minwidth
      }
      item.width = w
      state.set(key, item)
      tw += w
      if (item.col) {
        item.col.style.width = w + 'px'
      }
    })

    const minW = getContainerWidth()
    const tableWidth = minW > 0 ? Math.max(tw, minW) : tw
    syncFooterScrollWidth()
    elTable.value.style.tableLayout = 'fixed'
    elTable.value.style.width = tableWidth + 'px'
    elTable.value.style.minWidth = tableWidth + 'px'
    if (elTableWrap.value) {
      elTableWrap.value.style.width = tableWidth + 'px'
      elTableWrap.value.style.minWidth = tableWidth + 'px'
    }
    tableX = elTable.value.getBoundingClientRect().x
    // Не синхронизируем state с фактическими (растянутыми) ширинами при tableWidth > tw:
    // иначе после «минусовать все → сохранить → вернуть все → сохранить» растянутые ширины
    // попадали бы в конфиг и форма не возвращалась к исходным ширинам столбцов.
    syncColumnX()
    animationPause()
  },
  { immediate: false }
)

const { width } = useElementSize(elScroll)
watch(width, (a, b) => {
  const saved = listConfigModel.value?.list_config
  const vwratio = !b ? 1 : a / b

  // При ресайзе контейнера масштабируем только вручную растянутые столбцы.
  // Нельзя сбрасывать таблицу в width:100% — это убивает горизонтальный скролл
  // после первого layout (пока animationResume ещё не «закрепил» px-ширины).
  if (b && vwratio !== 1) {
    state.forEach((item, k) => {
      if (!item.resized || !item.width) {
        return
      }

      let w = item.width * vwratio
      item.width = w > saved?.[k]?.width ? w : saved[k].width
    })
  }
  syncColumnX()
  if (elTable.value) {
    animationResume()
    nextTick(() => {
      syncFooterScrollWidth()
      syncScrollPositions()
    })
  }
})

// При входе в режим настройки: снимаем явную ширину контейнера, сохраняем ширины столбцов, подписываемся на скролл для видимости ручки
watch(configMode, isConfig => {
  if (!isConfig) {
    rightEdgeScrollCleanup?.()
    rightHandleVisible.value = false
    // После закрытия «Настроить список» DOM <col> пересоздаётся — заново
    // применяем px-ширины и sync sticky-скролла (иначе скролл «появляется только после настройки»).
    nextTick(() => {
      colsRendered = 0
      animationResume()
      syncFooterScrollWidth()
      syncScrollPositions()
    })
    return
  }
  nextTick(() => {
    const cardEl = elTableCard.value ?? elScroll.value
    if (cardEl) {
      cardEl.style.removeProperty('width')
      cardEl.style.removeProperty('min-width')
      cardEl.style.removeProperty('max-width')
    }
    const keys = cols.value.map(c => c.title)
    const saved = {}
    keys.forEach(k => {
      saved[k] = Number(state.get(k)?.width) || MIN_COL_WIDTH
    })
    rightEdgeSavedWidths.value = saved
    attachRightEdgeScrollListener()
  })
})
watch(configMode, v => emit('configModeChange', v))
watch(
  () => cols.value?.length,
  (len, prev) => {
    if (len !== prev) colsRendered = 0
  }
)

watch(
  [() => props.columns],
  ([columns]) => {
    const newTitles = columns.map(c => c.title)
    const prev = lastColumnTitles.value
    if (prev && prev.length === newTitles.length && prev.every((t, i) => t === newTitles[i])) {
      return
    }
    lastColumnTitles.value = newTitles
    colsRendered = 0

    columns.forEach(c => {
      if (state.has(c.title)) {
        return
      }
      state.set(c.title, {})
    })

    if (columnOrder.value.length === 0) {
      columnOrder.value = columns.map(c => c.title)
    } else {
      const orderSet = new Set(columnOrder.value)
      const rest = columns.map(c => c.title).filter(t => !orderSet.has(t))
      if (rest.length) columnOrder.value = [...columnOrder.value, ...rest]
    }
  },
  { immediate: true, deep: true }
)

watch(
  listConfigModel,
  lc => {
    const c = lc?.list_config
    const titles = columnTitles.value
    const columnsByTitle = Object.fromEntries(props.columns.map(col => [col.title, col]))

    const applyDefaultWidths = () => {
      props.columns.forEach(col => {
        if (!titles.has(col.title)) return
        const item = state.get(col.title) || {}
        const defW = parseColumnWidth(col.width)
        if (defW != null) item.width = Math.max(defW, getMinColWidth(col.title))
        state.set(col.title, item)
      })
      syncColumnX()
      animationResume()
    }

    if (!c || typeof c !== 'object') {
      columnOrder.value = props.columns.map(col => col.title)
      applyDefaultWidths()
      return
    }

    const widthKeys = Object.keys(c).filter(k => k !== '_order' && titles.has(k))
    const hasNoWidths = widthKeys.length === 0 || widthKeys.every(key => c[key]?.width == null)
    if (hasNoWidths) {
      if (Array.isArray(c._order)) {
        const order = c._order.filter(t => titles.has(t))
        const rest = props.columns.map(col => col.title).filter(t => !order.includes(t))
        columnOrder.value = [...order, ...rest]
      } else {
        columnOrder.value = props.columns.map(col => col.title)
      }
      applyDefaultWidths()
      return
    }

    if (Array.isArray(c._order)) {
      const order = c._order.filter(t => titles.has(t))
      const rest = props.columns.map(col => col.title).filter(t => !order.includes(t))
      columnOrder.value = [...order, ...rest]
    } else {
      columnOrder.value = props.columns.map(col => col.title)
    }

    const h = []
    colsRendered = 0
    Object.keys(c).forEach(key => {
      if (key === '_order' || !titles.has(key)) return
      const item = state.get(key) || {}
      const cf = c[key]
      const col = columnsByTitle[key]
      const staticW = getStaticColumnWidth(key)
      if (staticW != null) {
        item.width = staticW
      } else if (cf?.width != null) {
        item.width = Math.max(Number(cf.width), getMinColWidth(key))
      } else {
        const defW = col ? parseColumnWidth(col.width) : null
        if (defW != null) item.width = Math.max(defW, getMinColWidth(key))
      }
      if (cf?.hidden) {
        h.push(key)
      }
      item.resized = cf.resized

      state.set(key, item)
    })
    let hFinal = [...new Set(h)]
    // Не применять конфиг, где скрыты все столбцы (для любого пользователя должен оставаться хотя бы один видимый)
    if (hFinal.length >= props.columns.length && props.columns.length > 0) {
      const order = columnOrder.value.length
        ? columnOrder.value
        : props.columns.map(col => col.title)
      const firstKey = order[0]
      if (firstKey) hFinal = hFinal.filter(k => k !== firstKey)
    }
    hidden.value = hFinal
    syncColumnX()
    colsRendered = 0
    nextTick(() => animationResume())
    nextTick(() => {
      syncFooterScrollWidth()
      syncScrollPositions()
    })
  },
  { immediate: true }
)

watch(fullscreen, (isFull, wasFull) => {
  const run = () => {
    if (isFull) {
      const cardEl = elTableCard.value ?? elScroll.value
      if (cardEl) {
        cardEl.style.removeProperty('width')
        cardEl.style.removeProperty('min-width')
        cardEl.style.removeProperty('max-width')
      }
    }
    if (!elTable.value) return
    let target = getContainerWidth()
    const total = getCurrentTotal()
    if (total <= 0) return
    if (isFull && !wasFull) {
      saveNormalModeWidths()
      if (target <= 0) {
        setTimeout(run, 50)
        return
      }
      if (total < target) scaleColumnsToTotal(target)
    } else if (!isFull && wasFull) {
      if (normalModeWidthsSnapshot) {
        restoreNormalModeWidths()
      } else if (target > 0 && total > target) {
        scaleColumnsToTotal(target)
      }
    }
  }
  nextTick(() => {
    if (isFull && !wasFull) {
      requestAnimationFrame(() => requestAnimationFrame(run))
    } else {
      run()
    }
  })
})

watch(
  elTable,
  async elTable => {
    const { x } = elTable.getBoundingClientRect()
    tableX = x

    elTable.querySelectorAll('th').forEach(ce => {
      const key = ce.dataset.key
      const { width, x } = ce.getBoundingClientRect()
      const col = props.columns.find(c => c.title === key)
      const defW = col ? parseColumnWidth(col.width) : null
      const w = defW != null ? defW : width

      const item = state.get(key) || {}
      item.width = w
      item.x = x

      state.set(key, item)
      initialState.set(key, { width: w, x })
    })

    await listConfigFetch()
  },
  { once: true }
)
function canHideColumn(col) {
  return !col.static && visibleNonStaticCount.value > 1
}
function openConfigMode() {
  configMode.value = true
}
function setColumnWidthForFilter(columnKey, widthOrNull) {
  const item = state.get(columnKey)
  if (!item) return
  if (widthOrNull === null || widthOrNull === undefined) {
    const saved = filterRestoreWidths.get(columnKey)
    if (saved !== undefined) {
      item.width = saved
      filterRestoreWidths.delete(columnKey)
    }
  } else {
    if (!filterRestoreWidths.has(columnKey)) {
      filterRestoreWidths.set(columnKey, item.width)
    }
    item.width = Math.max(widthOrNull, item.width || getMinColWidth(columnKey))
  }
  animationResume()
}
function getRowClickHandler() {
  const h = props.onRowClick ?? attrs['onClick:row'] ?? attrs['onClick:Row']
  return typeof h === 'function' ? h : null
}

function getContainerWidth() {
  return (elTableCard.value ?? elScroll.value)?.clientWidth ?? 0
}

function getCurrentTotal() {
  const keysOrdered = cols.value.map(col => col.title)
  return keysOrdered.reduce((sum, key) => sum + (Number(state.get(key)?.width) || MIN_COL_WIDTH), 0)
}

function saveNormalModeWidths() {
  const keysOrdered = cols.value.map(col => col.title)
  normalModeWidthsSnapshot = {}
  keysOrdered.forEach(key => {
    const w = Number(state.get(key)?.width) || MIN_COL_WIDTH
    normalModeWidthsSnapshot[key] = w
  })
}

function restoreNormalModeWidths() {
  if (!normalModeWidthsSnapshot) return
  const keysOrdered = cols.value.map(col => col.title)
  keysOrdered.forEach(key => {
    const saved = normalModeWidthsSnapshot[key]
    if (saved != null) {
      const it = state.get(key)
      if (it) {
        it.width = saved
        state.set(key, it)
      }
    }
  })
  normalModeWidthsSnapshot = null
  syncColumnX()
  animationResume()
}

function scaleColumnsToTotal(targetTotal) {
  const keysOrdered = cols.value.map(col => col.title)
  if (keysOrdered.length === 0) return
  const currentWidths = keysOrdered.map(key => Number(state.get(key)?.width) || MIN_COL_WIDTH)
  const currentTotal = currentWidths.reduce((a, b) => a + b, 0)
  if (currentTotal <= 0) return
  const factor = targetTotal / currentTotal
  const n = keysOrdered.length
  const newWidths = currentWidths.map((w, i) =>
    Math.max(getMinColWidth(keysOrdered[i]), Math.round(w * factor))
  )
  let sum = 0
  for (let i = 0; i < n - 1; i++) sum += newWidths[i]
  newWidths[n - 1] = Math.max(getMinColWidth(keysOrdered[n - 1]), targetTotal - sum)
  keysOrdered.forEach((key, i) => {
    const it = state.get(key)
    if (it) {
      it.width = newWidths[i]
      state.set(key, it)
    }
  })
  syncColumnX()
  animationResume()
}

function getMinColWidth(key) {
  return key === 'expense_amount' ? MIN_EXPENSE_AMOUNT_WIDTH : MIN_COL_WIDTH
}

/** Фиксированная ширина для статических колонок (expense_replace, expense_action, select_series_btn) — не меняется при ресайзе */
function getStaticColumnWidth(key) {
  const colDef = props.columns.find(c => c.title === key)
  if (!colDef?.static) return null
  const w = parseColumnWidth(colDef.width)
  return w != null ? w : MIN_COL_WIDTH
}

/** Ширину из описания колонки (число или строка '90px') приводим к числу пикселей */
function parseColumnWidth(v) {
  if (v == null) return null
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  const n = parseFloat(String(v))
  return Number.isNaN(n) ? null : n
}

function syncFooterScrollWidth() {
  const inner = footerScrollInner.value
  const table = elTableWrap.value ?? elTable.value
  const container = elTableCard.value ?? elScroll.value
  if (!inner || !table || !container) return

  const contentWidth = table.scrollWidth
  const minWidth = container.clientWidth + 1 // +1, чтобы полоса не схлопывалась
  inner.style.width = Math.max(contentWidth, minWidth) + 'px'
}

function syncScrollPositions() {
  const footer = footerScroll.value
  // Горизонтальный скролл — у .table-rtms (карточка), не у outer:
  // sticky-полоса синхронизируется с тем же элементом.
  const table = elTableCard.value ?? elScroll.value
  if (!footer || !table) return

  footerScrollCleanup?.()
  if (scrollRafId) {
    cancelAnimationFrame(scrollRafId)
    scrollRafId = null
  }

  let syncing = false

  const onFooterScroll = () => {
    if (syncing) return
    if (scrollRafId) {
      cancelAnimationFrame(scrollRafId)
    }
    scrollRafId = requestAnimationFrame(() => {
      syncing = true
      table.scrollLeft = footer.scrollLeft
      syncing = false
      scrollRafId = null
    })
  }

  const onTableScroll = () => {
    if (syncing) return
    if (scrollRafId) {
      cancelAnimationFrame(scrollRafId)
    }
    scrollRafId = requestAnimationFrame(() => {
      syncing = true
      footer.scrollLeft = table.scrollLeft
      syncing = false
      scrollRafId = null
    })
  }

  footer.addEventListener('scroll', onFooterScroll, { passive: true })
  table.addEventListener('scroll', onTableScroll, { passive: true })
  footerScrollCleanup = () => {
    if (scrollRafId) {
      cancelAnimationFrame(scrollRafId)
      scrollRafId = null
    }
    footer.removeEventListener('scroll', onFooterScroll)
    table.removeEventListener('scroll', onTableScroll)
    footerScrollCleanup = null
  }
}

function syncColumnX() {
  const keysOrdered = cols.value.map(col => col.title)
  let xx = tableX
  for (const key of keysOrdered) {
    const it = state.get(key)
    if (it) {
      it.x = xx
      xx += Number(it.width) || MIN_COL_WIDTH
      state.set(key, it)
    }
  }
}

function updateRightHandleVisible() {
  const card = elTableCard.value ?? elScroll.value
  if (!card) return
  const { scrollLeft, clientWidth, scrollWidth } = card
  const atRight =
    scrollWidth <= clientWidth + RIGHT_EDGE_EPS ||
    scrollLeft + clientWidth >= scrollWidth - RIGHT_EDGE_EPS
  rightHandleVisible.value = atRight
}
function attachRightEdgeScrollListener() {
  const card = elTableCard.value ?? elScroll.value
  if (!card || rightEdgeScrollCleanup) return
  updateRightHandleVisible()
  const onScroll = () => updateRightHandleVisible()
  card.addEventListener('scroll', onScroll, { passive: true })
  rightEdgeScrollCleanup = () => {
    card.removeEventListener('scroll', onScroll)
    rightEdgeScrollCleanup = null
  }
}

function applyRightEdgeResize(clientX) {
  const cardEl = elTableCard.value ?? elScroll.value
  if (rightEdgeStartClientX.value == null || rightEdgeStartContainerWidth.value == null || !cardEl)
    return
  const delta = clientX - rightEdgeStartClientX.value
  let w = rightEdgeStartContainerWidth.value + delta
  const keysOrdered = cols.value.map(col => col.title)
  const n = keysOrdered.length
  const minW = n * MIN_COL_WIDTH
  const gridEl = cardEl.closest('.rtms-grid')
  const containerW = cardEl.clientWidth ?? 0
  const gridW = gridEl?.clientWidth ?? 0
  const maxW = Math.max(containerW, gridW, 8000)
  w = Math.max(minW, Math.min(maxW, w))

  if (keysOrdered.length === 0) return
  const currentWidths = keysOrdered.map(key => Number(state.get(key)?.width) || MIN_COL_WIDTH)
  const currentTotal = currentWidths.reduce((a, b) => a + b, 0)
  const newWidths = currentWidths.map(wid => wid)
  const minTableW = n * MIN_COL_WIDTH
  const targetTotal = Math.max(minTableW, w)

  if (currentTotal > targetTotal) {
    let deficit = currentTotal - targetTotal
    for (let i = n - 1; i >= 0 && deficit > 0; i--) {
      const canTake = newWidths[i] - MIN_COL_WIDTH
      const take = Math.min(deficit, canTake)
      newWidths[i] -= take
      deficit -= take
    }
  } else if (currentTotal < targetTotal) {
    // При расширении: сначала восстанавливаем столбцы слева направо до сохранённых ширин, затем прирост — в последний.
    let add = targetTotal - currentTotal
    const saved = rightEdgeSavedWidths.value
    if (saved) {
      for (let i = 0; i < n && add > 0; i++) {
        const key = keysOrdered[i]
        const savedW = saved[key] ?? newWidths[i]
        const need = Math.max(0, savedW - newWidths[i])
        const give = Math.min(add, need)
        newWidths[i] += give
        add -= give
      }
    }
    if (add > 0) {
      newWidths[n - 1] = Math.max(MIN_COL_WIDTH, newWidths[n - 1] + add)
    }
  } else {
    return
  }

  keysOrdered.forEach((key, i) => {
    const it = state.get(key)
    if (it) {
      it.width = newWidths[i]
      state.set(key, it)
    }
  })
  let xx = tableX
  for (const key of keysOrdered) {
    const it = state.get(key)
    if (it) {
      it.x = xx
      xx += it.width
      state.set(key, it)
    }
  }
  animationResume()
  if (currentTotal < targetTotal) {
    nextTick(() => {
      const el = elTableCard.value ?? elScroll.value
      if (el && el.scrollWidth > el.clientWidth) {
        el.scrollLeft = el.scrollWidth - el.clientWidth
      }
    })
  }
}

function cleanupRightEdgeListeners() {
  if (rightEdgeHandleEl && rightEdgePointerMove) {
    rightEdgeHandleEl.removeEventListener('pointermove', rightEdgePointerMove)
    rightEdgePointerMove = null
  }
  rightEdgeHandleEl = null
  if (rightEdgePointerUp) {
    document.removeEventListener('pointerup', rightEdgePointerUp, true)
    document.removeEventListener('pointercancel', rightEdgePointerUp, true)
    rightEdgePointerUp = null
  }
}

onBeforeUnmount(() => {
  footerScrollCleanup?.()
  rightEdgeScrollCleanup?.()
  cleanupRightEdgeListeners()
})

function onRightEdgePointerDown(ev) {
  const handle = elTableRightHandle.value ?? ev.currentTarget
  const cardEl = elTableCard.value ?? elScroll.value
  if (!handle || !cardEl) return
  ev.preventDefault()
  rightEdgeDragging.value = true
  rightEdgeStartClientX.value = ev.clientX
  rightEdgeStartContainerWidth.value = getCurrentTotal()
  rightEdgePointerMove = e => applyRightEdgeResize(e.clientX)
  rightEdgePointerUp = () => {
    cleanupRightEdgeListeners()
    rightEdgeDragging.value = false
    rightEdgeStartClientX.value = null
    rightEdgeStartContainerWidth.value = null
    elTableCard.value?.classList.remove('resizing')
    elScroll.value?.classList.remove('resizing')
  }
  rightEdgeHandleEl = handle
  try {
    if (ev.pointerId != null) {
      handle.setPointerCapture(ev.pointerId)
    }
  } catch {
    //
  }
  handle.addEventListener('pointermove', rightEdgePointerMove)
  document.addEventListener('pointerup', rightEdgePointerUp, true)
  document.addEventListener('pointercancel', rightEdgePointerUp, true)
  elTableCard.value?.classList.add('resizing')
  elScroll.value?.classList.add('resizing')
}

function colRender(key, el) {
  let item = state.get(key)
  if (!item) {
    const colDef = props.columns.find(c => c.title === key)
    const defW = colDef ? parseColumnWidth(colDef.width) : null
    item = { width: Math.max(defW ?? MIN_COL_WIDTH, getMinColWidth(key)) }
  }
  item.col = el
  state.set(key, item)

  colsRendered++
  if (colsRendered === cols.value.length) {
    nextTick(() => animationResume())
    nextTick(() => {
      syncFooterScrollWidth()
      syncScrollPositions()
    })
  }
}
function dragRender(key, el) {
  let item = state.get(key) || {}
  item.drag = el

  if (el == null) {
    item.stop()
    item.drag = null
    return
  }

  const { x } = useDraggable(el, {
    axis: 'x',
    exact: true,
    preventDefault: true,
    stopPropagation: true,
    capture: true,
    onStart() {
      elScroll.value.classList.toggle('resizing')
    },
    onEnd() {
      elScroll.value.classList.toggle('resizing')
    },
  })

  item.stop = watchVizDrag(x, key)
}

function watchVizDrag(x, c) {
  return watch(x, x => {
    const keysOrdered = cols.value.map(col => col.title)
    const staticW = getStaticColumnWidth(c)
    const minWCur = staticW ?? getMinColWidth(c)

    const handleEdge = x + DRAG_HANDLE_HALF

    const item = state.get(c)
    const curW = Number(item.width) || minWCur
    item.width = curW
    item.resized = true
    let delta = handleEdge - (item.x + curW)
    let w = curW + delta

    if (staticW != null) {
      item.width = staticW
    } else if (w < minWCur) {
      w = minWCur
      delta = w - item.width
    }

    const idx = keysOrdered.indexOf(c)
    const nextKey = idx >= 0 && idx < keysOrdered.length - 1 ? keysOrdered[idx + 1] : null
    const nextItem = nextKey ? state.get(nextKey) : null
    const nextStaticW = nextKey ? getStaticColumnWidth(nextKey) : null
    const minWNext = nextStaticW ?? (nextKey ? getMinColWidth(nextKey) : MIN_COL_WIDTH)

    if (nextItem && nextKey) {
      const nextW = nextItem.width - delta
      if (nextW >= minWNext) {
        if (staticW == null) item.width = w
        nextItem.width = nextStaticW != null ? nextStaticW : nextW
        state.set(c, item)
        state.set(nextKey, nextItem)
      } else if (staticW == null) {
        item.width = w
        state.set(c, item)
      } else {
        state.set(c, item)
      }
    } else {
      if (staticW == null) {
        if (w < minWCur) w = minWCur
        item.width = w
        state.set(c, item)
      } else {
        state.set(c, item)
      }
    }

    let xx = tableX
    for (const key of keysOrdered) {
      const it = state.get(key)
      if (it) {
        it.x = xx
        xx += it.width
        state.set(key, it)
      }
    }

    animationResume()
  })
}

function columnShow({ currentTarget }) {
  const key = currentTarget.value
  const a = Array.from(hidden.value)
  const i = a.indexOf(key)

  currentTarget.selectedIndex = 0

  a.splice(i, 1)
  hidden.value = a

  // Чтобы после появления нового <col> сработало применение ширин (colRender -> animationResume)
  colsRendered = 0
  // Задать ширину по умолчанию, если в state ещё нет (восстановленный столбец мог не быть в сохранённом конфиге)
  const col = props.columns.find(c => c.title === key)
  const defW = col ? parseColumnWidth(col.width) : null
  if (defW != null) {
    const item = state.get(key) || {}
    item.width = Math.max(defW, getMinColWidth(key))
    state.set(key, item)
  }
}

function columnHide(key) {
  if (visibleNonStaticCount.value <= 1) return
  hidden.value = [...hidden.value, key]
}

function onColDragStart(ev, col) {
  if (ev.target.closest('a, button, input, select, textarea, .col-viz--drag')) {
    ev.preventDefault()
    return
  }
  draggedColumnTitle.value = col.title
  ev.dataTransfer.effectAllowed = 'move'
  ev.dataTransfer.setData('text/plain', col.title)
  ev.dataTransfer.setData('application/x-rtms-column', col.title)
  if (emptyDragImage) ev.dataTransfer.setDragImage(emptyDragImage, 0, 0)
}

function onColDragEnd() {
  draggedColumnTitle.value = null
  dropTargetTitle.value = null
}

function onColDragOver(ev, col) {
  ev.preventDefault()
  ev.dataTransfer.dropEffect = 'move'
  const fromTitle = draggedColumnTitle.value
  if (!fromTitle || fromTitle === col.title) return
  const order = [...columnOrder.value]
  const fromIdx = order.indexOf(fromTitle)
  const toIdx = order.indexOf(col.title)
  if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return

  const th = elTable.value?.querySelector(`thead tr th[data-key="${col.title}"]`)
  if (!th) return
  const rect = th.getBoundingClientRect()
  const draggedW = Number(state.get(fromTitle)?.width) || MIN_COL_WIDTH
  const x = ev.clientX
  if (toIdx > fromIdx) {
    if (x < rect.right - draggedW) return
  } else {
    if (x > rect.left + draggedW) return
  }

  order.splice(fromIdx, 1)
  let newToIdx = order.indexOf(col.title)
  if (toIdx > fromIdx) newToIdx += 1
  order.splice(newToIdx, 0, fromTitle)
  columnOrder.value = order
  dropTargetTitle.value = fromTitle
  animationResume()
}

function onColDrop(ev) {
  ev.preventDefault()
  dropTargetTitle.value = null
  draggedColumnTitle.value = null
  animationResume()
}

async function saveListConfig() {
  let cfg = toValue(listConfigModel) || {}
  if (!cfg.id && props.listName) {
    await listConfigFetch()
    cfg = toValue(listConfigModel) || {}
  }
  const h = toValue(hidden)
  const c = {}

  state.forEach((v, k) => {
    const path = props.columns.find(col => col.title == k)?.path
    console.log(v)
    c[k] = {
      width: v.width,
      path,
      resized: v.resized || false,
    }
  })
  h.forEach(k => {
    const path = props.columns.find(col => col.title == k)?.path
    const savedW = state.get(k)?.width
    c[k] = { hidden: true, path, ...(savedW != null ? { width: savedW } : {}) }
  })
  c._order = columnOrder.value

  listConfigModel.value = Object.assign(
    {},
    {
      list_config: c,
      list_name: props.listName,
    }
  )

  await createListConfig({
    method: cfg.id ? 'put' : 'post',
    url: cfg.id ? `list_configs/${cfg.id}` : 'list_configs',
  })
  configMode.value = false
}
</script>

<template>
  <div
    class="rtms-grid"
    :class="{ 'col-drag-active': !!draggedColumnTitle }"
  >
    <List
      :columns="columns"
      :total="total"
      v-model="model"
      :pagination="pagination !== false"
      :persist="persist !== false"
      :page-size-options="pageSizeOptions"
      :persist-key="persistKey"
      :hide-filters-block="configButtonInHeader"
    >
      <template #filters>
        <button
          v-if="!configButtonInHeader && listName && total && !configMode"
          type="button"
          class="btn btn-secondary"
          @click="configMode = true"
          :title="t('configureList')"
        >
          <i class="bi bi-gear-fill"></i>
        </button>
      </template>
      <template
        v-if="$slots.pagerExtra"
        #pagerExtra
      >
        <slot name="pagerExtra" />
      </template>

      <div
        class="hstack gap-2 mt-2"
        v-if="configMode"
      >
        <button
          type="button"
          class="btn btn-secondary text-nowrap"
          :disabled="listConfigCreating"
          @click="saveListConfig"
        >
          <i class="bi bi-floppy2-fill"></i>
          {{ t('saveListSettings') }}
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="configMode = false"
        >
          {{ t('common.close') }}
        </button>

        <select
          class="form-select"
          :disabled="!hidden.length"
          @input="columnShow"
        >
          <option
            disabled
            selected
          >
            {{ t('showColumns') }}
          </option>
          <option
            v-for="col of colsHidden"
            :key="col.title"
            :value="col.title"
          >
            {{ col.label }}
          </option>
        </select>
      </div>

      <div
        class="table-rtms-outer"
        ref="el-scroll"
      >
        <div
          class="table-rtms table-rtms--hide-native-scrollbar"
          :class="{ 'table-rtms--config': configMode }"
          ref="el-table-card"
        >
          <div
            class="rtms-table-wrap"
            ref="el-table-wrap"
          >
            <table
              class="table table-striped table-hover"
              ref="el-table"
            >
              <colgroup>
                <col
                  v-for="c in cols"
                  :key="c.title"
                  :data-key="c.title"
                  :ref="el => colRender(c.title, el)"
                />
              </colgroup>

              <thead>
                <tr>
                  <th
                    class="col-viz--cell"
                    :class="{
                      'col-viz--dragged': configMode && draggedColumnTitle === c.title,
                      'col-viz--draggable': configMode && !c.static,
                      'col-viz--cell--has-filter':
                        configMode && !noHeaderFilters && !c.static && c.filter !== false,
                    }"
                    v-for="(c, colIndex) of cols"
                    :key="c.title"
                    :data-key="c.title"
                    :draggable="configMode && !c.static"
                    @dragstart="configMode && !c.static && onColDragStart($event, c)"
                    @dragend="onColDragEnd"
                    @dragover="onColDragOver($event, c)"
                    @drop="onColDrop($event, c)"
                    @dragleave="dropTargetTitle = null"
                    :title="configMode && !c.static ? t('editColumn') : undefined"
                  >
                    <template v-if="configMode">
                      <div class="col-viz--head-inner">
                        <span
                          v-if="!c.static"
                          class="col-viz--head-actions"
                        >
                          <span class="col-viz--hide-btn">
                            <button
                              type="button"
                              class="btn btn-link p-0"
                              :disabled="!canHideColumn(c)"
                              @click="columnHide(c.title)"
                              :title="
                                canHideColumn(c) ? t('hideColumn') : t('keepOneColumn')
                              "
                            >
                              <i class="bi bi-dash-circle"></i>
                            </button>
                          </span>
                        </span>
                        <span class="text-nowrap">{{ c.label }}</span>
                      </div>
                    </template>
                    <template v-else>
                      <template v-if="c.static">{{ c.label }}</template>
                      <Thd
                        v-else
                        :headless="true"
                        :column-id="c.path || c.title"
                        :column-key="c.title"
                        :is-bool="c.is_bool"
                        :filter="noHeaderFilters ? false : c.filter !== false"
                        :filter-options="
                          Array.isArray(c.filter?.options) ? c.filter.options : undefined
                        "
                      >
                        {{ c.label }}
                      </Thd>
                    </template>

                    <div
                      class="col-viz--drag"
                      v-if="configMode && !c.static && colIndex < cols.length - 1"
                      :ref="el => dragRender(c.title, el)"
                    ></div>
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row of data"
                  :key="row.reserve_expense_id != null ? `${row.id}-${row.reserve_expense_id}` : row.id"
                  :role="rowClickable && 'link'"
                  :class="[
                    {
                      'cursor-pointer': rowClickable,
                      'table-secondary': row.archive,
                    },
                    typeof props.rowClass === 'function' ? props.rowClass(row) : props.rowClass,
                  ]"
                  @click="getRowClickHandler()?.(row)"
                >
                  <td
                    v-for="c in cols"
                    :key="c.title"
                    :class="{
                      'col-viz--dragged-cell': configMode && draggedColumnTitle === c.title,
                      'col-viz--draggable-cell': configMode && !c.static,
                      'col-viz--cell-expense-amount': c.title === 'expense_amount',
                    }"
                    :draggable="configMode && !c.static"
                    @dragstart="configMode && !c.static && onColDragStart($event, c)"
                    @dragend="onColDragEnd"
                    @dragover="configMode && onColDragOver($event, c)"
                    @drop="configMode && onColDrop($event, c)"
                    @dragleave="configMode && (dropTargetTitle = null)"
                  >
                    <slot
                      name="cell"
                      :row="row"
                      :col="c"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div
          v-if="configMode && cols.length"
          ref="table-right-handle"
          class="col-viz--drag-right-edge"
          :class="{ 'col-viz--drag-right-edge-visible': rightHandleVisible }"
          :title="t('resizeTable')"
          @pointerdown="onRightEdgePointerDown"
        />
      </div>
      <div
        class="rtms-sticky-scroll"
        ref="footerScroll"
      >
        <div
          class="rtms-sticky-scroll-inner"
          ref="footerScrollInner"
        ></div>
      </div>
    </List>
  </div>
</template>

<i18n>
{
  "ru": {
    "configureList": "Настроить список",
    "saveListSettings": "Сохранить настройки списка",
    "showColumns": "Показывать столбцы…",
    "editColumn": "Изменить столбец",
    "hideColumn": "Скрыть столбец",
    "keepOneColumn": "Должен остаться хотя бы один настраиваемый столбец",
    "resizeTable": "Изменить ширину таблицы"
  },
  "en": {
    "configureList": "Configure list",
    "saveListSettings": "Save list settings",
    "showColumns": "Show columns…",
    "editColumn": "Edit column",
    "hideColumn": "Hide column",
    "keepOneColumn": "At least one configurable column must remain",
    "resizeTable": "Resize table"
  }
}
</i18n>
<style scoped>
.rtms-grid {
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
}

/*
 * Outer не должен быть scrollport: иначе скроллится оболочка,
 * а sticky-полоса крутит .table-rtms — и горизонтальный скролл «ломается».
 * Скролл только у карточки .table-rtms (см. main.scss overflow: auto).
 */
.table-rtms-outer {
  overflow: visible;
  position: relative;
  max-width: 100%;
  min-width: 0;
}

.rtms-sticky-scroll {
  position: sticky;
  bottom: 0;
  overflow-x: auto;
  overflow-y: hidden;
  background: #fff;
  z-index: 5;
}

.rtms-sticky-scroll::-webkit-scrollbar {
  height: 14px;
}

.rtms-sticky-scroll-inner {
  height: 1px;
}

.table-rtms--hide-native-scrollbar {
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  -ms-overflow-style: none; /* IE/Edge legacy */
  scrollbar-width: none; /* Firefox */
}

.table-rtms--hide-native-scrollbar::-webkit-scrollbar {
  height: 0;
  width: 0;
}
</style>
